import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App, URLOpenListenerEvent } from '@capacitor/app';

// Platform detection
export const isNativePlatform = () => Capacitor.isNativePlatform();

// Redirect URIs
export const getRedirectUri = (webRedirectUri: string): string => {
  if (isNativePlatform()) {
    return 'bestihealth://auth-callback';
  }
  return webRedirectUri;
};

export const getLogoutRedirectUri = (webRedirectUri: string): string => {
  if (isNativePlatform()) {
    return 'bestihealth://signout';
  }
  return webRedirectUri;
};

/**
 * Setup mobile auth deep link listener for Capacitor apps.
 * This listens for the custom URL scheme callback from Cognito.
 * 
 * @param onAuthCallback - Callback function that receives the full callback URL
 * @returns Cleanup function to remove the listener
 */
export const setupMobileAuthListener = (
  onAuthCallback: (url: string) => void
): (() => void) => {
  if (!isNativePlatform()) {
    // No-op on web
    return () => {};
  }

  let listenerHandle: any = null;

  // Setup listener (returns a Promise)
  App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
    const url = event.url;
    
    // Check if this is an auth callback
    if (url.startsWith('bestihealth://auth-callback') || url.startsWith('bestihealth://signout')) {
      // Close the browser
      await Browser.close().catch(() => {});
      
      // Call the callback with the URL
      onAuthCallback(url);
    }
  }).then(handle => {
    listenerHandle = handle;
  });

  // Return cleanup function
  return () => {
    if (listenerHandle) {
      listenerHandle.remove();
    }
  };
};

/**
 * Open the Cognito authorization URL in the system browser on mobile,
 * or redirect on web.
 * 
 * @param authUrl - The full Cognito authorization URL
 */
export const openAuthUrl = async (authUrl: string): Promise<void> => {
  if (isNativePlatform()) {
    // Open in system browser on mobile
    await Browser.open({ 
      url: authUrl, 
      presentationStyle: 'fullscreen',
      windowName: '_self'
    });
  } else {
    // Standard redirect on web
    window.location.href = authUrl;
  }
};

/**
 * Helper to check if a URL is a mobile auth callback
 */
export const isMobileAuthCallback = (url: string): boolean => {
  return url.startsWith('bestihealth://auth-callback') || url.startsWith('bestihealth://');
};

